import axios, {AxiosError} from "axios";
import {UserPaymentMethod} from "../Models/Interfaces/UserPaymentMethod";

// Models
import {SubscriptionUpgradeDTO} from "../Models/DTOs/Subscription";

const BACKEND_URL = process.env.REACT_APP_WEB_API_URL ?? "/"; // adjust if needed

// -----------------------------------------------------------------------------
// POST /billing/stripe/create-setup-intent
// Body: { user_id, payment_method_id }
// -----------------------------------------------------------------------------
export async function CreateSetupIntent(
    userId: string,
    accessToken: string,
    abortController: AbortController
): Promise<{ client_secret: string; customer_id: string }> {
    const res = await axios.post<{ client_secret: string; customer_id: string }>(
        BACKEND_URL + "v1/billing/stripe/create-setup-intent",
        { user_id: userId },
        {
            signal: abortController.signal,
            headers: {Authorization: "Bearer " + accessToken},
        }
    );
    return res.data;
}

// POST /billing/stripe/payment-methods/set-default
export interface SetDefaultPaymentMethodResponse {
    default_payment_method: string;
    detached_others?: boolean;
    invoice_id?: string;
    invoice_paid?: boolean;
}

export async function SetDefaultPaymentMethod(
    userId: string,
    paymentMethodId: string,
    detachOthers = true,
    accessToken: string,
    abortController: AbortController,
    tryPayLatest = true
): Promise<SetDefaultPaymentMethodResponse> {
    const res = await axios.post<SetDefaultPaymentMethodResponse>(
        BACKEND_URL + "v1/billing/stripe/payment-methods/set-default",
        {
            user_id: userId,
            payment_method_id: paymentMethodId,
            detach_others: detachOthers,
            try_pay_latest: tryPayLatest
        },
        {
            signal: abortController.signal,
            headers: {Authorization: "Bearer " + accessToken},
        }
    );
    return res.data;
}

// -----------------------------------------------------------------------------
// POST /billing/stripe/create-subscription
// Body: {
//   email, plan_id, user_id, plan_type, payment_method_id,
//   offer_key?: string, promo?: string
//   price_id?/coupon_id? are temporary rollout fallbacks only
// }
// -----------------------------------------------------------------------------
export interface CreateSubscriptionResponse {
    user_id: string;
    stripe_customer_id?: string;
    subscription_id: string;
    status: string;
    client_secret?: string; // Optional, present if 3DS is needed
    payment_intent_status?: string;
    reused?: boolean;
    recovered?: boolean;
}

export async function CreateSubscription(
    email: string,
    planId: number,
    aspNetUserId: string,
    planType: number,
    accessToken: string, // <--- REQUIRED: backend enforces self-or-admin on this endpoint
    abortController: AbortController,
    opts?: {
        priceId?: string;
        couponId?: string | null;
        offerKey?: string;
        offerId?: number;
        offerVersion?: number;
        finalAmountMinor?: number;
        promotionCode?: string;
        paymentMethodId?: string; // <--- ADD THIS
        attemptId?: string;
        affiliateExpectedAmountMinor?: number;
    }
): Promise<CreateSubscriptionResponse | AxiosError> {
    try {
        const res = await axios.post<CreateSubscriptionResponse>(
            BACKEND_URL + "v1/billing/stripe/create-subscription",
            {
                email,
                plan_id: planId,
                user_id: aspNetUserId,
                plan_type: planType,
                ...(opts?.offerKey
                    ? {
                        offer_key: opts.offerKey,
                        offer_id: opts.offerId,
                        offer_version: opts.offerVersion,
                        final_amount_minor: opts.finalAmountMinor,
                        ...(opts.promotionCode && {promo: opts.promotionCode}),
                    }
                    : {
                        ...(opts?.priceId && {price_id: opts.priceId}),
                        ...(opts?.couponId && {coupon_id: opts.couponId}),
                    }),
                payment_method_id: opts?.paymentMethodId, // <--- PASS IT HERE
                attempt_id: opts?.attemptId,
                ...(opts?.affiliateExpectedAmountMinor != null && {
                    affiliate_expected_amount_minor: opts.affiliateExpectedAmountMinor,
                }),
            },
            {
                signal: abortController.signal,
                headers: {Authorization: "Bearer " + accessToken},
            }
        );
        return res.data;
    } catch (err) {
        return err as AxiosError;
    }
}

export interface CancellationDiscountOption {
    eligible: boolean;
    offer_key: string | null;
    /** Present for catalog offers and echoed on acceptance to bind the displayed snapshot. */
    offer_id?: number | null;
    offer_version?: number | null;
    pricing_mode?: "coupon" | "prepaid" | null;
    intro_interval?: "month" | "year" | null;
    intro_interval_count?: number | null;
    /** The next renewal, when a prepaid retention offer is charged and starts. */
    starts_at_utc?: string | null;
    amount_off_minor: number | null;
    base_amount_minor?: number | null;
    final_amount_minor?: number | null;
    currency: string | null;
    coupon_duration: "once" | "repeating" | "forever" | string | null;
    coupon_duration_months: number | null;
    next_eligible_at_utc: string | null;
    tenure_segment?: "any" | "under_6_months" | "at_least_6_months" | string | null;
    headline?: string | null;
    badge?: string | null;
    description?: string | null;
    terms?: string | null;
    cta_label?: string | null;
}

export interface CancellationPauseOption {
    eligible: boolean;
    allowed_duration_months: number[];
    ineligible_reason: string | null;
}

export interface CancellationReasonOption {
    code: string;
    label: string;
}

export interface CancellationOptions {
    subscription_id?: string;
    cadence?: string;
    currency?: string;
    discount: CancellationDiscountOption;
    pause: CancellationPauseOption;
    cancellation: {
        effective_at_utc: string | null;
        reasons: CancellationReasonOption[];
    };
}

export interface RetentionOfferAcceptResponse {
    accepted: boolean;
    already_processed?: boolean;
    subscription_id?: string;
    offer?: CancellationDiscountOption;
}

export interface CancelStripeSubscriptionOptions {
    reasonCode?: string;
    comment?: string;
    requestId?: string;
}

export interface CancelStripeSubscriptionResponse {
    subscription_id: string;
    cancelled: boolean;
    immediate: boolean;
    request_id?: string;
    already_processed?: boolean;
    cancels_at_utc?: string;
}

// -----------------------------------------------------------------------------
// GET /billing/stripe/cancellation-options?user_id=...
// -----------------------------------------------------------------------------
export async function GetCancellationOptions(
    aspNetUserId: string,
    accessToken: string,
    abortController: AbortController
): Promise<CancellationOptions | AxiosError> {
    try {
        const res = await axios.get<CancellationOptions>(
            BACKEND_URL + "v1/billing/stripe/cancellation-options?user_id=" + encodeURIComponent(aspNetUserId),
            {
                signal: abortController.signal,
                headers: {Authorization: "Bearer " + accessToken},
            }
        );
        return res.data;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

// -----------------------------------------------------------------------------
// POST /billing/stripe/retention-offer/accept
// -----------------------------------------------------------------------------
export async function AcceptRetentionOffer(
    aspNetUserId: string,
    offerKey: string,
    requestId: string,
    accessToken: string,
    abortController: AbortController,
    snapshot?: Pick<
        CancellationDiscountOption,
        "offer_id" | "offer_version" | "final_amount_minor"
    >
): Promise<RetentionOfferAcceptResponse | AxiosError> {
    try {
        const res = await axios.post<RetentionOfferAcceptResponse>(
            BACKEND_URL + "v1/billing/stripe/retention-offer/accept",
            {
                user_id: aspNetUserId,
                offer_key: offerKey,
                request_id: requestId,
                ...(snapshot?.offer_id != null && {offer_id: snapshot.offer_id}),
                ...(snapshot?.offer_version != null && {offer_version: snapshot.offer_version}),
                ...(snapshot?.final_amount_minor != null && {
                    final_amount_minor: snapshot.final_amount_minor,
                }),
            },
            {signal: abortController.signal, headers: {Authorization: "Bearer " + accessToken}}
        );
        return res.data;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

// -----------------------------------------------------------------------------
// POST /billing/stripe/cancel
// Body: { user_id, now, reason_code?, comment?, request_id? }
// -----------------------------------------------------------------------------
export async function CancelStripeSubscription(
    aspNetUserId: string,
    immediate: boolean,
    accessToken: string, // <--- REQUIRED: backend enforces self-or-admin
    abortController: AbortController,
    options?: CancelStripeSubscriptionOptions
): Promise<CancelStripeSubscriptionResponse | AxiosError> {
    try {
        const res = await axios.post(
            BACKEND_URL + "v1/billing/stripe/cancel",
            {
                user_id: aspNetUserId,
                now: immediate,
                ...(options?.reasonCode !== undefined && {reason_code: options.reasonCode}),
                ...(options?.comment !== undefined && {comment: options.comment}),
                ...(options?.requestId !== undefined && {request_id: options.requestId}),
            },
            {signal: abortController.signal, headers: {Authorization: "Bearer " + accessToken}}
        );
        return res.data as CancelStripeSubscriptionResponse;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

export async function ResumeSubscription(
    aspNetUserId: string,
    accessToken: string, // <--- REQUIRED: backend enforces self-or-admin
    abortController: AbortController
): Promise<{ subscription_id: string; resumed: boolean } | AxiosError> {
    try {
        const res = await axios.post(
            BACKEND_URL + "v1/billing/stripe/resume",
            {user_id: aspNetUserId},
            {signal: abortController.signal, headers: {Authorization: "Bearer " + accessToken}}
        );
        return res.data as { subscription_id: string; resumed: boolean };
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

// -----------------------------------------------------------------------------
// POST /billing/stripe/pause  (requires Authorization: Bearer)
// Body: { user_id, duration_months: 1 | 3 | 6 }
// Pauses an active monthly subscription. The member keeps access through the
// period they've already paid for (until access_until), then membership freezes
// (no billing, no access) until resumes_at, when billing/access auto-resume.
// -----------------------------------------------------------------------------
export interface PauseSubscriptionResponse {
    subscription_id: string;
    paused: boolean;
    access_until: string; // RFC3339 — same value as SubscriptionDTO.PauseAccessUntilUtc
    resumes_at: string;   // RFC3339 — same value as SubscriptionDTO.PauseResumesAtUtc
    behavior: string;     // e.g. "void"
}

export async function PauseSubscription(
    aspNetUserId: string,
    durationMonths: number,
    accessToken: string,
    abortController: AbortController
): Promise<PauseSubscriptionResponse | AxiosError> {
    try {
        const res = await axios.post(
            BACKEND_URL + "v1/billing/stripe/pause",
            {user_id: aspNetUserId, duration_months: durationMonths},
            {signal: abortController.signal, headers: {Authorization: "Bearer " + accessToken}}
        );
        return res.data as PauseSubscriptionResponse;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

// -----------------------------------------------------------------------------
// POST /billing/stripe/unpause  (requires Authorization: Bearer)
// Body: { user_id }
// Resumes a paused subscription early. NOTE: this is NOT ResumeSubscription
// (which un-cancels a scheduled cancel). fresh_start=true means they resumed
// during the freeze and are charged today; tenure_reset=true means their
// "member since" date was reset.
// -----------------------------------------------------------------------------
export interface UnpauseSubscriptionResponse {
    subscription_id: string;
    unpaused: boolean;
    fresh_start: boolean;
    tenure_reset: boolean;
}

export async function UnpauseSubscription(
    aspNetUserId: string,
    accessToken: string,
    abortController: AbortController
): Promise<UnpauseSubscriptionResponse | AxiosError> {
    try {
        const res = await axios.post(
            BACKEND_URL + "v1/billing/stripe/unpause",
            {user_id: aspNetUserId},
            {signal: abortController.signal, headers: {Authorization: "Bearer " + accessToken}}
        );
        return res.data as UnpauseSubscriptionResponse;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

// -----------------------------------------------------------------------------
// POST /billing/stripe/restart
// Body: { user_id, plan_type?, offer_key?, promo?, payment_method_id? }
// price_id is a temporary rollout fallback; restarts never send a raw coupon.
// Returns 200 with { subscription_id, status } OR 409 with
// { error: "no_default_payment_method", customer_id, message }
// -----------------------------------------------------------------------------
export interface RestartSubscriptionResponse {
    subscription_id: string;
    status: string;
    user_id?: string;
    stripe_customer_id?: string;
    client_secret?: string;
    payment_intent_status?: string;
    recovered?: boolean;
    error?: string;
    message?: string;
}

export async function RestartSubscription(
    aspNetUserId: string,
    accessToken: string, // <--- REQUIRED: backend enforces self-or-admin
    abortController: AbortController,
    opts?: {
        planType?: number;
        priceId?: string;
        offerKey?: string;
        offerId?: number;
        offerVersion?: number;
        finalAmountMinor?: number;
        promotionCode?: string;
        paymentMethodId?: string;
    }
): Promise<RestartSubscriptionResponse | AxiosError> { // <--- Update Type
    try {
        const res = await axios.post<RestartSubscriptionResponse>(
            BACKEND_URL + "v1/billing/stripe/restart",
            {
                user_id: aspNetUserId,
                plan_type: opts?.planType,
                ...(opts?.offerKey
                    ? {
                        offer_key: opts.offerKey,
                        offer_id: opts.offerId,
                        offer_version: opts.offerVersion,
                        final_amount_minor: opts.finalAmountMinor,
                        ...(opts.promotionCode && {promo: opts.promotionCode}),
                    }
                    : {
                        ...(opts?.priceId && {price_id: opts.priceId}),
                    }),
                payment_method_id: opts?.paymentMethodId
            },
            { signal: abortController.signal, headers: {Authorization: "Bearer " + accessToken} }
        );
        return res.data;
    } catch (err: unknown) {
        if (axios.isAxiosError(err)) return err;
        return new AxiosError("Non-Axios error", "ERR_UNKNOWN");
    }
}

export async function ListPaymentMethods(
    userId: string,
    accessToken: string,
    abortController?: AbortController
): Promise<UserPaymentMethod[] | AxiosError> {
    if (!userId || !userId.trim()) throw new Error("missing_user_id");
    try {
        const res = await axios.get<UserPaymentMethod[] | { payment_methods: UserPaymentMethod[] }>(
            BACKEND_URL + `v1/billing/stripe/payment-methods?user_id=${userId}`,
            {
                withCredentials: true,
                signal: abortController?.signal,
                headers: {Authorization: "Bearer " + accessToken},
            }
        );

        // Some handlers return a bare array; others wrap it
        const raw = Array.isArray(res.data) ? res.data : res.data?.payment_methods ?? [];

        // Normalize fields just in case (expMonth vs exp_month, isDefault vs is_default)
        return raw.map((pm: any) => ({
            id: pm.id,
            brand: pm.brand,
            last4: pm.last4,
            exp_month: pm.exp_month ?? pm.expMonth,
            exp_year: pm.exp_year ?? pm.expYear,
            is_default: pm.is_default ?? pm.isDefault ?? false,
        })) as UserPaymentMethod[];
    } catch (err) {
        return err as AxiosError;
    }
}

export interface UpgradeOfferSelection {
    targetPlanKey: string;
    offerKey?: string;
    offerId?: number;
    offerVersion?: number;
    finalAmountMinor?: number;
    /** Temporary standard-price fallback used only when no catalog offer exists. */
    legacyPriceId?: string;
}

export async function PreviewUpgrade(
    userId: string,
    selection: UpgradeOfferSelection,
    accessToken: string, // <--- REQUIRED: backend enforces self-or-admin
    abort?: AbortController
): Promise<SubscriptionUpgradeDTO> {
    const payload: any = {
        user_id: userId,
        target_plan_key: selection.targetPlanKey,
        preview_only: true,
        ...(selection.offerKey
            ? {
                offer_key: selection.offerKey,
                offer_id: selection.offerId,
                offer_version: selection.offerVersion,
                final_amount_minor: selection.finalAmountMinor,
            }
            : selection.legacyPriceId
                ? {price_id: selection.legacyPriceId}
                : {}),
    };

    const res = await axios.post<SubscriptionUpgradeDTO>(
        `${BACKEND_URL}v1/billing/stripe/upgrade`,
        payload,
        { withCredentials: true, signal: abort?.signal, headers: {Authorization: "Bearer " + accessToken} }
    );
    return res.data;
}

export async function ApplyUpgrade(
    userId: string,
    selection: UpgradeOfferSelection,
    accessToken: string, // <--- REQUIRED: backend enforces self-or-admin
    abort?: AbortController,
    invoiceId?: string // <--- ADD THIS
): Promise<SubscriptionUpgradeDTO> {
    const isContinuation = !!invoiceId?.trim();
    const payload: any = {
        user_id: userId,
        target_plan_key: selection.targetPlanKey,
        preview_only: false,
        ...(!isContinuation
            ? selection.offerKey
                ? {
                    offer_key: selection.offerKey,
                    offer_id: selection.offerId,
                    offer_version: selection.offerVersion,
                    final_amount_minor: selection.finalAmountMinor,
                }
                : selection.legacyPriceId
                    ? {price_id: selection.legacyPriceId}
                    : {}
            : {}),
        invoice_id: invoiceId // <--- PASS IT
    };

    const res = await axios.post<SubscriptionUpgradeDTO>(
        `${BACKEND_URL}v1/billing/stripe/upgrade`,
        payload,
        { withCredentials: true, signal: abort?.signal, headers: {Authorization: "Bearer " + accessToken} }
    );
    return res.data;
}
